import { useMemo, useState } from 'react';

import AddressAutocomplete from '../AddressAutocomplete';
import { createCustomer, fetchCustomers } from './calendarApi';
import RecurringAppointmentControls from './RecurringAppointmentControls';
import RecurringOccurrencePreview from './RecurringOccurrencePreview';
import useRecurringAppointments from './useRecurringAppointments';
import { AppointmentShareControls } from './AppointmentModal';

const TERMS_OPTIONS = ['COD', 'NET15', 'NET30', 'NET45', 'NET60', 'DUEONRECEIPT'];

const EMPTY_QUICK_CUSTOMER = {
  company: '',
  contact: '',
  email: '',
  office_phone: '',
  mobile_phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  terms: 'COD',
  notes: '',
};

const DEFAULT_INITIAL_STATE = {
  appointment: {
    customer_id: null,
    customer_name: '',
    appointment_type_id: null,
    job_type_id: null,
    employee_id: null,
    title: 'Appointment',
    description: '',
    start_time: '09:00',
    end_time: '10:00',
    status: 'scheduled',
    notes: '',
    service_street: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    details: '',
    estimate_on_file: false,
  },
  recurrence: {
    anchor_date: '',
    frequency: '',
    count: '',
    end_date: null,
  },
  overrides: [],
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '3px solid #ffffff',
  borderRadius: 12,
  background: '#ffffff',
  color: '#000000',
  fontWeight: 900,
  boxSizing: 'border-box',
};

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minWidth: 0,
};

const labelStyle = {
  fontWeight: 900,
  textTransform: 'uppercase',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: 10,
};

const sectionStyle = {
  border: '3px solid #E8E2DA',
  borderRadius: 14,
  background: '#365C91',
  padding: 12,
  boxSizing: 'border-box',
};

const actionButtonStyle = {
  minHeight: 44,
  padding: '10px 16px',
  border: '3px solid #E8761D',
  borderRadius: 12,
  background: '#000000',
  color: '#E8C98F',
  fontWeight: 900,
  cursor: 'pointer',
  maxWidth: '100%',
  boxSizing: 'border-box',
};

const formatUSPhone = (value) => {
  const digits = String(value || '').replace(/\D+/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const customerLabel = (customer) => String(
  customer?.name || customer?.company || customer?.contactPerson || ''
).trim();

const customerRecord = (id, payload) => ({
  id,
  name: payload.name,
  company: payload.company || payload.name,
  contactPerson: payload.contactPerson,
  billingEmail: payload.billingEmail,
  phone: payload.phone,
  office_phone: payload.phone,
  mobile_phone: payload.mobile_phone,
  street: payload.street,
  city: payload.city,
  state: payload.state,
  zip: payload.zip,
  terms: payload.terms,
  notes: payload.notes,
});

const findCreatedCustomer = (customers, payload) => {
  const wantedName = String(payload.name || '').trim().toLowerCase();
  const wantedEmail = String(payload.billingEmail || '').trim().toLowerCase();
  const wantedPhone = String(payload.phone || '').replace(/\D+/g, '').slice(-10);
  const wantedMobile = String(payload.mobile_phone || '').replace(/\D+/g, '').slice(-10);

  return customers.find((customer) => {
    const name = customerLabel(customer).toLowerCase();
    const email = String(customer?.billingEmail || customer?.email || '').trim().toLowerCase();
    const phone = String(customer?.phone || customer?.office_phone || '')
      .replace(/\D+/g, '').slice(-10);
    const mobile = String(customer?.mobile_phone || '').replace(/\D+/g, '').slice(-10);
    if (wantedEmail && email === wantedEmail) return true;
    if (wantedPhone && phone === wantedPhone) return true;
    if (wantedMobile && mobile === wantedMobile) return true;
    return Boolean(wantedName && name === wantedName);
  });
};

const RecurringAppointmentModal = ({
  isOpen,
  onClose,
  customers = [],
  employees = [],
  jobTypes = [],
  styles = {},
  onCustomerCreated,
  onCalendarRefresh,
  initialState = DEFAULT_INITIAL_STATE,
}) => {
  const recurring = useRecurringAppointments(initialState);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickCustomer, setQuickCustomer] = useState(EMPTY_QUICK_CUSTOMER);
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddError, setQuickAddError] = useState(null);
  const [createdCustomers, setCreatedCustomers] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);
  const [calendarRefreshError, setCalendarRefreshError] = useState(null);
  const [reconciliationRefreshed, setReconciliationRefreshed] = useState(false);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [useCustomerAddress, setUseCustomerAddress] = useState(false);

  const displayedCustomers = useMemo(() => {
    const supplied = Array.isArray(customers) ? customers : [];
    const suppliedIds = new Set(supplied.map((customer) => String(customer.id)));
    return [
      ...createdCustomers.filter((customer) => !suppliedIds.has(String(customer.id))),
      ...supplied,
    ];
  }, [createdCustomers, customers]);

  const selectedCustomer = useMemo(() => displayedCustomers.find((customer) => (
    String(customer.id) === String(recurring.appointment.customer_id)
  )) || null, [displayedCustomers, recurring.appointment.customer_id]);

  if (!isOpen) return null;

  const setAppointmentField = (field, value) => {
    recurring.setAppointmentDetails((current) => ({ ...current, [field]: value }));
  };

  const selectCustomer = (customerId) => {
    const selected = displayedCustomers.find((customer) => (
      String(customer.id) === String(customerId)
    ));
    recurring.setAppointmentDetails((current) => {
      const next = {
        ...current,
        customer_id: selected ? Number(selected.id) : null,
        customer_name: selected ? customerLabel(selected) : '',
        title: selected ? customerLabel(selected) || 'Appointment' : 'Appointment',
      };
      if (selected && useCustomerAddress) {
        next.service_street = String(selected.street || '').trim();
        next.service_city = String(selected.city || '').trim();
        next.service_state = String(selected.state || '').trim().slice(0, 2);
        next.service_zip = String(selected.zip || '').trim();
      }
      return next;
    });
  };

  const applySelectedCustomerAddress = () => {
    const selected = displayedCustomers.find((customer) => (
      String(customer.id) === String(recurring.appointment.customer_id)
    ));
    if (!selected) return;
    recurring.setAppointmentDetails((current) => ({
      ...current,
      service_street: String(selected.street || '').trim(),
      service_city: String(selected.city || '').trim(),
      service_state: String(selected.state || '').trim().slice(0, 2),
      service_zip: String(selected.zip || '').trim(),
    }));
  };

  const changeQuickCustomer = (field, value) => {
    const nextValue = field === 'office_phone' || field === 'mobile_phone'
      ? formatUSPhone(value)
      : value;
    setQuickCustomer((current) => ({ ...current, [field]: nextValue }));
  };

  const saveQuickCustomer = async () => {
    if (quickAddLoading) return;
    if (recurring.creationLoading || recurring.creationOutcomeUncertain) {
      setQuickAddError('Customer changes are unavailable until recurring creation is reconciled.');
      return;
    }
    setQuickAddLoading(true);
    setQuickAddError(null);

    const payload = {
      company: String(quickCustomer.company || '').trim(),
      name: String(quickCustomer.company || quickCustomer.contact || '').trim(),
      contactPerson: String(quickCustomer.contact || '').trim(),
      billingEmail: String(quickCustomer.email || '').trim(),
      phone: String(quickCustomer.office_phone || '').trim(),
      office_phone: String(quickCustomer.office_phone || '').trim(),
      mobile_phone: String(quickCustomer.mobile_phone || '').trim(),
      street: String(quickCustomer.street || '').trim(),
      city: String(quickCustomer.city || '').trim(),
      state: String(quickCustomer.state || '').trim(),
      zip: String(quickCustomer.zip || '').trim(),
      terms: String(quickCustomer.terms || 'COD').trim(),
      notes: String(quickCustomer.notes || '').trim(),
    };

    let customerWasCreated = false;
    try {
      const created = await createCustomer(payload);
      customerWasCreated = true;
      let newId = created?.id || created?.customer_id;
      let createdCustomer = newId ? customerRecord(newId, payload) : null;

      if (!newId) {
        const refreshedCustomers = await fetchCustomers();
        createdCustomer = findCreatedCustomer(refreshedCustomers, payload) || null;
        newId = createdCustomer?.id;
      }
      if (!newId || !createdCustomer) {
        throw new Error('Created customer could not be identified.');
      }

      setCreatedCustomers((current) => {
        if (current.some((customer) => String(customer.id) === String(newId))) return current;
        return [createdCustomer, ...current];
      });
      recurring.setAppointmentDetails((current) => ({
        ...current,
        customer_id: Number(newId),
        customer_name: customerLabel(createdCustomer),
        title: customerLabel(createdCustomer) || 'Appointment',
        ...(useCustomerAddress ? {
          service_street: String(createdCustomer.street || '').trim(),
          service_city: String(createdCustomer.city || '').trim(),
          service_state: String(createdCustomer.state || '').trim().slice(0, 2),
          service_zip: String(createdCustomer.zip || '').trim(),
        } : {}),
      }));
      setQuickCustomer(EMPTY_QUICK_CUSTOMER);
      setShowQuickAdd(false);
      if (typeof onCustomerCreated === 'function') {
        try {
          await onCustomerCreated(createdCustomer);
        } catch (_error) {
          setModalMessage(
            'The customer was created and selected, but the parent customer list could not be refreshed.'
          );
        }
      }
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'CUSTOMER ADDED!' }
      }));
    } catch (_error) {
      setQuickAddError(
        customerWasCreated
          ? 'The customer may have been created, but it could not be identified. Refresh Customer Records before trying again.'
          : 'Unable to add the customer. Review the details and try again.'
      );
    } finally {
      setQuickAddLoading(false);
    }
  };

  const closeModal = () => {
    if (
      recurring.isCreationLocked()
      || recurring.creationLoading
      || recurring.creationOutcomeUncertain
    ) {
      setModalMessage(
        'Creation is pending or requires reconciliation. Refresh and confirm the Calendar before closing.'
      );
      return;
    }
    recurring.reset(initialState);
    setShowQuickAdd(false);
    setQuickCustomer(EMPTY_QUICK_CUSTOMER);
    setQuickAddError(null);
    setModalMessage(null);
    setCalendarRefreshError(null);
    setReconciliationRefreshed(false);
    setUseCustomerAddress(false);
    if (typeof onClose === 'function') onClose();
  };

  const createSeries = async () => {
    setModalMessage(null);
    setCalendarRefreshError(null);
    const result = await recurring.createRecurringSeries();
    if (!result) return;

    if (typeof onCalendarRefresh === 'function') {
      try {
        await onCalendarRefresh();
      } catch (_error) {
        setCalendarRefreshError(
          'Appointments were created, but the Calendar could not be refreshed. Refresh it manually.'
        );
      }
    }
  };

  const refreshForReconciliation = async () => {
    if (reconciliationLoading || typeof onCalendarRefresh !== 'function') return;
    setReconciliationLoading(true);
    setCalendarRefreshError(null);
    try {
      await onCalendarRefresh();
      setReconciliationRefreshed(true);
    } catch (_error) {
      setCalendarRefreshError(
        'The Calendar could not be refreshed. The recurring creation lock remains active.'
      );
    } finally {
      setReconciliationLoading(false);
    }
  };

  const confirmReconciliation = () => {
    if (!reconciliationRefreshed) return;
    const unlocked = recurring.reconcileCreationOutcome();
    if (unlocked) {
      setReconciliationRefreshed(false);
      setModalMessage('Prior creation outcome reconciled. Generate a new preview before creating.');
    } else {
      setReconciliationRefreshed(false);
      setModalMessage('Creation is still pending. Refresh the Calendar again before confirmation.');
    }
  };

  const disabledActionStyle = (disabled) => ({
    ...actionButtonStyle,
    width: 'min(100%, 340px)',
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  const centeredActionStyle = {
    ...actionButtonStyle,
    width: 'min(100%, 340px)',
  };
  const createActionStyle = (disabled) => ({
    ...disabledActionStyle(disabled),
    background: disabled ? '#4f5f55' : '#16733a',
    borderColor: disabled ? '#9b9b9b' : '#E8E2DA',
    color: disabled ? '#d0d0d0' : '#ffffff',
  });
  const cancelActionStyle = {
    ...centeredActionStyle,
    background: '#ffffff',
    borderColor: '#000000',
    color: '#000000',
  };
  const actionRowStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    width: '100%',
  };
  const hoverIn = (event) => {
    event.currentTarget.style.background = '#FFC107';
    event.currentTarget.style.color = '#000000';
  };
  const hoverOut = (event) => {
    event.currentTarget.style.background = '#000000';
    event.currentTarget.style.color = '#E8C98F';
  };
  const createHoverOut = (event) => {
    event.currentTarget.style.background = '#16733a';
    event.currentTarget.style.color = '#ffffff';
  };
  const cancelHoverOut = (event) => {
    event.currentTarget.style.background = '#ffffff';
    event.currentTarget.style.color = '#000000';
  };

  return (
    <div
      role="presentation"
      style={styles.modalOverlay || {
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        background: 'rgba(0, 0, 0, 0.7)',
        boxSizing: 'border-box',
      }}
      onMouseDown={closeModal}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="recurring-appointment-title"
        style={styles.modalCard || {
          width: '100%',
          maxWidth: 980,
          maxHeight: 'calc(100vh - 24px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '3px solid #E8E2DA',
          borderRadius: 18,
          background: '#365C91',
          color: '#ffffff',
          boxSizing: 'border-box',
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div
          style={styles.modalBody || {
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: 12,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <style>{`
            @media (max-width: 1023px) {
              .jf-recurring-mobile-action {
                width: 100% !important;
                max-width: none !important;
                min-width: 0 !important;
                min-height: 48px !important;
                box-sizing: border-box !important;
                font-size: 16px !important;
                font-weight: 900 !important;
              }
            }
          `}</style>
          <div id="recurring-appointment-title" style={{ fontWeight: 900, fontSize: 20, textTransform: 'uppercase' }}>
            New Recurring Appointment
          </div>

          {modalMessage ? (
            <div role="status" style={{ ...sectionStyle, fontWeight: 900 }}>
              {modalMessage}
            </div>
          ) : null}

          <RecurringAppointmentControls
            appointment={recurring.appointment}
            recurrence={recurring.recurrence}
            setAppointmentDetails={recurring.setAppointmentDetails}
            setRecurrenceSettings={recurring.setRecurrenceSettings}
            onDatePreview={recurring.requestDatePreview}
            datePreviewDates={recurring.datePreviewDates}
            datePreviewLoading={recurring.datePreviewLoading}
            datePreviewError={recurring.datePreviewError}
            styles={styles}
          />

          <section style={styles.section || sectionStyle} aria-label="Customer selection">
            <div style={{ ...labelStyle, marginBottom: 10 }}>Customer</div>
            <div style={gridStyle}>
              <label htmlFor="recurring-customer" style={fieldStyle}>
                <span style={labelStyle}>Existing Customer</span>
                <select
                  id="recurring-customer"
                  style={styles.select || inputStyle}
                  value={recurring.appointment.customer_id ?? ''}
                  onChange={(event) => selectCustomer(event.target.value)}
                >
                  <option value="">-- SELECT CUSTOMER --</option>
                  {displayedCustomers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customerLabel(customer) || '(no name)'}
                    </option>
                  ))}
                </select>
              </label>
              <div style={actionRowStyle}>
                <button
                  type="button"
                  className="jf-recurring-mobile-action"
                  style={centeredActionStyle}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                  onClick={() => setShowQuickAdd((current) => !current)}
                >
                  QUICK ADD NEW CUSTOMER
                </button>
              </div>
            </div>

            {showQuickAdd ? (
              <div style={{ ...(styles.section || sectionStyle), marginTop: 12, background: '#24324a' }}>
                <div style={gridStyle}>
                  <input style={styles.input || inputStyle} placeholder="CUSTOMER NAME / COMPANY" value={quickCustomer.company} onChange={(event) => changeQuickCustomer('company', event.target.value)} />
                  <input style={styles.input || inputStyle} placeholder="CONTACT (OPTIONAL)" value={quickCustomer.contact} onChange={(event) => changeQuickCustomer('contact', event.target.value)} />
                  <input style={styles.input || inputStyle} placeholder="EMAIL" value={quickCustomer.email} onChange={(event) => changeQuickCustomer('email', event.target.value)} />
                  <input style={styles.input || inputStyle} placeholder="OFFICE PHONE" value={quickCustomer.office_phone} onChange={(event) => changeQuickCustomer('office_phone', event.target.value)} inputMode="tel" />
                  <input style={styles.input || inputStyle} placeholder="MOBILE PHONE" value={quickCustomer.mobile_phone} onChange={(event) => changeQuickCustomer('mobile_phone', event.target.value)} inputMode="tel" />
                  <select style={styles.select || inputStyle} value={quickCustomer.terms} onChange={(event) => changeQuickCustomer('terms', event.target.value)} aria-label="Customer payment terms">
                    {TERMS_OPTIONS.map((term) => <option key={term} value={term}>{term}</option>)}
                  </select>
                </div>
                <div style={{ ...gridStyle, marginTop: 10 }}>
                  <AddressAutocomplete
                    inputStyle={styles.input || inputStyle}
                    placeholder="STREET"
                    value={quickCustomer.street}
                    onChange={(value) => changeQuickCustomer('street', value)}
                    onAddressSelect={({ street, city, state, zip }) => {
                      setQuickCustomer((current) => ({
                        ...current,
                        street,
                        city,
                        state: state.slice(0, 2),
                        zip,
                      }));
                    }}
                  />
                  <input style={styles.input || inputStyle} placeholder="CITY" value={quickCustomer.city} onChange={(event) => changeQuickCustomer('city', event.target.value)} />
                  <input style={styles.input || inputStyle} placeholder="STATE" value={quickCustomer.state} onChange={(event) => changeQuickCustomer('state', event.target.value)} />
                  <input style={styles.input || inputStyle} placeholder="ZIP" value={quickCustomer.zip} onChange={(event) => changeQuickCustomer('zip', event.target.value)} />
                </div>
                <textarea style={{ ...(styles.textarea || inputStyle), minHeight: 90, marginTop: 10 }} placeholder="CUSTOMER NOTES" value={quickCustomer.notes} onChange={(event) => changeQuickCustomer('notes', event.target.value)} />
                {quickAddError ? <div role="alert" style={{ marginTop: 10, fontWeight: 900 }}>{quickAddError}</div> : null}
                <div style={{ ...actionRowStyle, marginTop: 10 }}>
                  <button
                    type="button"
                    className="jf-recurring-mobile-action"
                    style={disabledActionStyle(
                      quickAddLoading
                      || recurring.creationLoading
                      || recurring.creationOutcomeUncertain
                    )}
                    disabled={
                      quickAddLoading
                      || recurring.creationLoading
                      || recurring.creationOutcomeUncertain
                    }
                    onClick={saveQuickCustomer}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                  >
                    {quickAddLoading ? 'SAVING…' : 'SAVE CUSTOMER'}
                  </button>
                  <button type="button" className="jf-recurring-mobile-action" style={centeredActionStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut} onClick={() => setShowQuickAdd(false)}>
                    CANCEL
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          <section style={styles.section || sectionStyle} aria-label="Job details and assignment">
            <div style={{ ...labelStyle, marginBottom: 10 }}>Job Details / Assignment</div>
            <div style={styles.row2 || gridStyle}>
              <div>
                <label htmlFor="recurring-employee" style={fieldStyle}>
                  <span style={labelStyle}>Employee</span>
                  <select
                    id="recurring-employee"
                    style={styles.select || inputStyle}
                    value={recurring.appointment.employee_id ?? ''}
                    onChange={(event) => setAppointmentField(
                      'employee_id',
                      event.target.value ? Number(event.target.value) : null
                    )}
                  >
                    <option value="">-- SELECT EMPLOYEE --</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {String(employee.name || employee.full_name || '').trim() || `Employee ${employee.id}`}
                      </option>
                    ))}
                  </select>
                </label>
                <div style={{ height: 10 }} />
                <label htmlFor="recurring-job-type" style={fieldStyle}>
                  <span style={labelStyle}>Job Type</span>
                <select
                  id="recurring-job-type"
                  style={styles.select || inputStyle}
                  value={recurring.appointment.job_type_id ?? ''}
                  onChange={(event) => {
                    const id = event.target.value ? Number(event.target.value) : null;
                    recurring.setAppointmentDetails((current) => ({
                      ...current,
                      appointment_type_id: id,
                      job_type_id: id,
                    }));
                  }}
                >
                  <option value="">-- SELECT JOB TYPE --</option>
                  {jobTypes.map((jobType) => (
                    <option key={jobType.id} value={jobType.id}>
                      {String(jobType.name || jobType.title || '').trim() || '(no name)'}
                    </option>
                  ))}
                </select>
                </label>
                <div style={{ height: 10 }} />
                <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontWeight: 900 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(recurring.appointment.estimate_on_file)}
                    onChange={(event) => setAppointmentField('estimate_on_file', event.target.checked)}
                    style={{ width: 20, height: 20 }}
                  />{' '}
                  ESTIMATE ON FILE
                </label>
              </div>
              <label htmlFor="recurring-details" style={fieldStyle}>
                <span style={labelStyle}>Details / Notes</span>
                <textarea id="recurring-details" style={styles.textarea || { ...inputStyle, minHeight: 120 }} placeholder="DETAILS OF JOB / LARGE NOTES" value={recurring.appointment.details || ''} onChange={(event) => setAppointmentField('details', event.target.value)} />
              </label>
            </div>
          </section>

          <section style={styles.section || sectionStyle} aria-label="Service address">
            <div style={{ ...labelStyle, marginBottom: 10 }}>Service Address</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontWeight: 900 }}>
              <input
                type="checkbox"
                checked={useCustomerAddress}
                disabled={!recurring.appointment.customer_id}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setUseCustomerAddress(checked);
                  if (checked) applySelectedCustomerAddress();
                }}
                style={{ width: 20, height: 20 }}
              />
              SERVICE ADDRESS SAME AS CUSTOMER ADDRESS
              {!recurring.appointment.customer_id ? ' (select customer first)' : ''}
            </label>
            <div style={styles.row4 || gridStyle}>
              <AddressAutocomplete
                inputStyle={styles.input || inputStyle}
                placeholder="STREET"
                value={recurring.appointment.service_street || ''}
                onChange={(value) => setAppointmentField('service_street', value)}
                onAddressSelect={({ street, city, state, zip }) => {
                  recurring.setAppointmentDetails((current) => ({
                    ...current,
                    service_street: street,
                    service_city: city,
                    service_state: state.slice(0, 2),
                    service_zip: zip,
                  }));
                }}
              />
              <input style={styles.input || inputStyle} placeholder="CITY" value={recurring.appointment.service_city || ''} onChange={(event) => setAppointmentField('service_city', event.target.value)} />
              <input style={styles.input || inputStyle} placeholder="ST" value={recurring.appointment.service_state || ''} onChange={(event) => setAppointmentField('service_state', event.target.value)} />
              <input style={styles.input || inputStyle} placeholder="ZIP" value={recurring.appointment.service_zip || ''} onChange={(event) => setAppointmentField('service_zip', event.target.value)} />
            </div>
          </section>

          {recurring.previewError ? (
            <div role="alert" style={{ ...(styles.section || sectionStyle), fontWeight: 900, textAlign: 'center' }}>
              {recurring.previewError}
            </div>
          ) : null}

          {!recurring.creationResult ? (
            <div style={{ ...actionRowStyle, margin: '20px 0 18px' }}>
              <button
                type="button"
                className="jf-recurring-mobile-action"
                style={{ ...disabledActionStyle(recurring.previewLoading), width: '100%' }}
                disabled={recurring.previewLoading}
                onMouseEnter={recurring.previewLoading ? undefined : hoverIn}
                onMouseLeave={hoverOut}
                onClick={recurring.requestPreview}
              >
                {recurring.previewLoading ? 'REVIEWING…' : 'REVIEW APPOINTMENTS & WARNINGS'}
              </button>
            </div>
          ) : null}

          {!recurring.creationResult ? (
            <div style={{
              marginBottom: 22,
              ...(!recurring.previewResult ? { fontSize: 'clamp(14px, 1.5vw, 15px)' } : {}),
            }}>
              <RecurringOccurrencePreview
                previewResult={recurring.previewResult}
                previewLoading={recurring.previewLoading}
                employees={employees}
                setOccurrenceOverrides={recurring.setOccurrenceOverrides}
              />
            </div>
          ) : null}

          {recurring.creationError ? (
            <div role="alert" style={{ ...(styles.section || sectionStyle), fontWeight: 900 }}>
              {recurring.creationError}
            </div>
          ) : null}
          {recurring.creationResult ? (
            <div
              role="status"
              aria-live="polite"
              style={{
                ...(styles.section || sectionStyle),
                border: '3px solid #E8761D',
                background: '#000000',
                color: '#ffffff',
                fontWeight: 900,
                textAlign: 'center',
                marginBottom: 20,
              }}
            >
              <div style={{ color: '#E8C98F', fontSize: 'clamp(18px, 5vw, 26px)', overflowWrap: 'anywhere' }}>
                SUCCESS! {recurring.creationResult.occurrenceCount} RECURRING APPOINTMENTS CREATED!
              </div>
              <div style={{ marginTop: 8 }}>
                Your appointments have been saved and added to the calendar.
              </div>
            </div>
          ) : null}
          {calendarRefreshError ? (
            <div role="alert" style={{ ...(styles.section || sectionStyle), fontWeight: 900 }}>
              {calendarRefreshError}
            </div>
          ) : null}

          {recurring.creationLoading || recurring.creationOutcomeUncertain ? (
            <section style={{ ...(styles.section || sectionStyle), background: '#24324a' }}>
              <div style={{ fontWeight: 900 }}>
                Creation is pending or unresolved. Do not retry until the Calendar has been refreshed and the earlier outcome confirmed.
              </div>
              <div style={{ ...actionRowStyle, marginTop: 10 }}>
                <button
                  type="button"
                  className="jf-recurring-mobile-action"
                  style={disabledActionStyle(
                    reconciliationLoading || typeof onCalendarRefresh !== 'function'
                  )}
                  disabled={reconciliationLoading || typeof onCalendarRefresh !== 'function'}
                  onMouseEnter={reconciliationLoading ? undefined : hoverIn}
                  onMouseLeave={hoverOut}
                  onClick={refreshForReconciliation}
                >
                  {reconciliationLoading ? 'REFRESHING…' : 'REFRESH CALENDAR'}
                </button>
                <button
                  type="button"
                  className="jf-recurring-mobile-action"
                  style={disabledActionStyle(!reconciliationRefreshed)}
                  disabled={!reconciliationRefreshed}
                  onMouseEnter={!reconciliationRefreshed ? undefined : hoverIn}
                  onMouseLeave={hoverOut}
                  onClick={confirmReconciliation}
                >
                  I CONFIRMED THE CALENDAR OUTCOME
                </button>
              </div>
            </section>
          ) : null}

          <div style={{ ...actionRowStyle, gap: 16, marginTop: 0, padding: '0 4px 4px', boxSizing: 'border-box' }}>
            {!recurring.creationResult ? (
              <button
                type="button"
                className="jf-recurring-mobile-action"
                style={createActionStyle(
                  !recurring.canCreate
                  || recurring.creationLoading
                  || recurring.creationOutcomeUncertain
                )}
                disabled={
                  !recurring.canCreate
                  || recurring.creationLoading
                  || recurring.creationOutcomeUncertain
                }
                onMouseEnter={recurring.canCreate && !recurring.creationLoading && !recurring.creationOutcomeUncertain ? hoverIn : undefined}
                onMouseLeave={recurring.canCreate && !recurring.creationLoading && !recurring.creationOutcomeUncertain ? createHoverOut : undefined}
                onClick={createSeries}
              >
                {recurring.creationLoading ? 'CREATING…' : 'CREATE RECURRING APPOINTMENTS'}
              </button>
            ) : null}
            <button
              type="button"
              className="jf-recurring-mobile-action"
              style={recurring.creationResult ? centeredActionStyle : cancelActionStyle}
              onMouseEnter={hoverIn}
              onMouseLeave={recurring.creationResult ? hoverOut : cancelHoverOut}
              onClick={closeModal}
            >
              {recurring.creationResult ? 'CLOSE & VIEW CALENDAR' : 'CANCEL'}
            </button>
          </div>
          {recurring.creationResult?.appointmentId ? (
            <AppointmentShareControls
              appointmentId={recurring.creationResult.appointmentId}
              customerMobile={String(selectedCustomer?.mobile_phone || '').trim()}
              customerEmail={String(selectedCustomer?.billingEmail || selectedCustomer?.email || '').trim()}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RecurringAppointmentModal;
